/*
 * Entity sub-section routing helpers — UI-free (no JSX).
 *
 * Generalizes the prototype's `EntitySubMenuManager` URL logic
 * (`public/js/entity-sub-menu.js`: `buildEntityUrl` / `parseEntityUrl` /
 * `resolveSection`) into a domain-free core the composed `EntitySectionLayout`
 * and the consuming feature route share. It carries no domain words: the caller
 * supplies the section list and the base path.
 *
 * The prototype's canonical URL shape is `/{type}/{id}/{section}` with a
 * fallback to the first section (its `resolveSection` defaults to `profile`,
 * which is every entity type's first section). We preserve that shape and the
 * fallback while dropping the hard-coded `profile` default — the fallback is
 * "the first configured section", so it holds for any section set.
 */

/** A single sub-section within an entity detail view. */
export interface EntitySection {
  /** URL-safe id, e.g. `profile`, `bank-accounts`. */
  id: string;
  /** Human label shown in the sub-menu, e.g. `Bank Accounts`. */
  label: string;
}

/**
 * Build the path for a section under a base path.
 * `buildSectionPath('/clients/42', 'members')` → `/clients/42/members`.
 * The base path is taken verbatim; trailing slashes are normalized.
 */
export function buildSectionPath(basePath: string, sectionId: string): string {
  const base = basePath.replace(/\/+$/, '');
  return `${base}/${sectionId}`;
}

/**
 * Resolve a (possibly missing or invalid) section id against the configured
 * sections, falling back to the first section. Mirrors the prototype's
 * `resolveSection`, but the fallback is the first configured section rather
 * than a literal `profile`, so it generalizes to any section set.
 *
 * Returns `undefined` only when there are no sections at all.
 */
export function resolveSection(
  sectionId: string | undefined,
  sections: readonly EntitySection[],
): string | undefined {
  const first = sections[0];
  if (!first) return undefined;
  const match = sectionId
    ? sections.find((s) => s.id === sectionId)
    : undefined;
  return match ? match.id : first.id;
}

/**
 * Whether a section id is a valid member of the configured sections.
 */
export function isValidSection(
  sectionId: string | undefined,
  sections: readonly EntitySection[],
): boolean {
  return !!sectionId && sections.some((s) => s.id === sectionId);
}
